(() => {
  if (window.RSDownloadBulkLoaded) return;
  window.RSDownloadBulkLoaded = true;

  document.addEventListener("DOMContentLoaded", () => {
  const textEncoder = new TextEncoder();
  const textDecoder = new TextDecoder();
  const imageDownloadMessageTimers = new WeakMap();
  const productListPromises = new Map();
  const SUCCESS_ICON_SVG = '<svg t="1781592261537" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4974" width="200" height="200"><path d="M512 0C230.4 0 0 230.4 0 512s230.4 512 512 512 512-230.4 512-512-512-512-512-512zM409.6 761.6L172.8 544l57.6-64L409.6 640l384-384 64 64-448 441.6z" fill="green" p-id="4975"></path></svg>';

  function readUint16(bytes, offset) {
    return bytes[offset] | (bytes[offset + 1] << 8);
  }

  function readUint32(bytes, offset) {
    return (
      bytes[offset] |
      (bytes[offset + 1] << 8) |
      (bytes[offset + 2] << 16) |
      (bytes[offset + 3] << 24)
    ) >>> 0;
  }

  function writeUint16(bytes, offset, value) {
    bytes[offset] = value & 255;
    bytes[offset + 1] = (value >>> 8) & 255;
  }

  function writeUint32(bytes, offset, value) {
    bytes[offset] = value & 255;
    bytes[offset + 1] = (value >>> 8) & 255;
    bytes[offset + 2] = (value >>> 16) & 255;
    bytes[offset + 3] = (value >>> 24) & 255;
  }

  const crcTable = Array.from({ length: 256 }, (_, index) => {
    let crc = index;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    return crc >>> 0;
  });

  function crc32(bytes) {
    let crc = 0xffffffff;
    bytes.forEach((byte) => {
      crc = crcTable[(crc ^ byte) & 255] ^ (crc >>> 8);
    });
    return (crc ^ 0xffffffff) >>> 0;
  }

  async function inflateRaw(bytes) {
    if (!window.DecompressionStream) {
      throw new Error("This browser cannot read compressed XLSX files.");
    }
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  async function unzipXlsx(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    let eocdOffset = -1;

    for (let index = bytes.length - 22; index >= 0; index -= 1) {
      if (readUint32(bytes, index) === 0x06054b50) {
        eocdOffset = index;
        break;
      }
    }

    if (eocdOffset < 0) throw new Error("Invalid XLSX file.");

    const totalEntries = readUint16(bytes, eocdOffset + 10);
    const centralOffset = readUint32(bytes, eocdOffset + 16);
    const files = {};
    let pointer = centralOffset;

    for (let entry = 0; entry < totalEntries; entry += 1) {
      if (readUint32(bytes, pointer) !== 0x02014b50) break;

      const method = readUint16(bytes, pointer + 10);
      const compressedSize = readUint32(bytes, pointer + 20);
      const fileNameLength = readUint16(bytes, pointer + 28);
      const extraLength = readUint16(bytes, pointer + 30);
      const commentLength = readUint16(bytes, pointer + 32);
      const localOffset = readUint32(bytes, pointer + 42);
      const name = textDecoder.decode(bytes.slice(pointer + 46, pointer + 46 + fileNameLength));

      const localNameLength = readUint16(bytes, localOffset + 26);
      const localExtraLength = readUint16(bytes, localOffset + 28);
      const dataStart = localOffset + 30 + localNameLength + localExtraLength;
      const compressed = bytes.slice(dataStart, dataStart + compressedSize);
      if (method !== 0 && method !== 8) {
        throw new Error(`Unsupported XLSX compression method: ${method}`);
      }
      files[name] = method === 0 ? compressed : await inflateRaw(compressed);

      pointer += 46 + fileNameLength + extraLength + commentLength;
    }

    return files;
  }

  function readFileText(files, name) {
    const content = files[name];
    if (typeof content === "string") return content;
    if (content instanceof Uint8Array) return textDecoder.decode(content);
    if (content instanceof ArrayBuffer) return textDecoder.decode(new Uint8Array(content));
    return "";
  }

  function xmlEscape(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function xmlUnescape(value) {
    return String(value ?? "")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, number) => String.fromCharCode(parseInt(number, 10)));
  }

  function colToIndex(col) {
    return col.split("").reduce((sum, char) => sum * 26 + char.charCodeAt(0) - 64, 0) - 1;
  }

  function colName(index) {
    let name = "";
    let value = index + 1;
    while (value > 0) {
      const mod = (value - 1) % 26;
      name = String.fromCharCode(65 + mod) + name;
      value = Math.floor((value - mod) / 26);
    }
    return name;
  }

  function parseSharedStrings(xml = "") {
    return Array.from(xml.matchAll(/<si\b[\s\S]*?<\/si>/g)).map((match) => {
      return Array.from(match[0].matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g))
        .map((textMatch) => xmlUnescape(textMatch[1]))
        .join("");
    });
  }

  function parseSheetRows(xml = "", sharedStrings = []) {
    return parseSheetRowRecords(xml, sharedStrings).map((record) => record.values);
  }

  function parseSheetRowRecords(xml = "", sharedStrings = []) {
    return Array.from(xml.matchAll(/<row\b[^>]*>[\s\S]*?<\/row>/g)).map((rowMatch) => {
      const rowXml = rowMatch[0];
      const rowNumber = parseInt(rowXml.match(/<row\b[^>]*\br="(\d+)"/)?.[1] || "0", 10);
      const row = [];
      Array.from(rowXml.matchAll(/<c\b([^>]*)>([\s\S]*?)<\/c>/g)).forEach((cellMatch) => {
        const attrs = cellMatch[1];
        const body = cellMatch[2];
        const ref = attrs.match(/\br="([A-Z]+)\d+"/)?.[1];
        const type = attrs.match(/\bt="([^"]+)"/)?.[1];
        const index = ref ? colToIndex(ref) : row.length;
        let value = "";

        if (type === "inlineStr") {
          value = Array.from(body.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g))
            .map((textMatch) => xmlUnescape(textMatch[1]))
            .join("");
        } else {
          const raw = body.match(/<v>([\s\S]*?)<\/v>/)?.[1] || "";
          value = type === "s" ? sharedStrings[parseInt(raw, 10)] || "" : xmlUnescape(raw);
        }

        row[index] = value;
      });
      return {
        rowXml,
        rowNumber,
        values: row.map((value) => value ?? "")
      };
    });
  }

  function xmlAttributeValue(attrs, name) {
    return attrs.match(new RegExp(`\\b${name}="([^"]*)"`))?.[1]
      || attrs.match(new RegExp(`\\b${name}='([^']*)'`))?.[1]
      || "";
  }

  function workbookSheetEntries(files) {
    const workbookXml = readFileText(files, "xl/workbook.xml");
    const workbookRelsXml = readFileText(files, "xl/_rels/workbook.xml.rels");
    const relationshipTargets = new Map();

    Array.from(workbookRelsXml.matchAll(/<Relationship\b([^>]*)\/?>/g)).forEach((match) => {
      const attrs = match[1];
      const id = xmlAttributeValue(attrs, "Id");
      const target = xmlAttributeValue(attrs, "Target").replace(/^\/+/, "");
      if (!id || !target) return;
      relationshipTargets.set(id, target.startsWith("xl/") ? target : resolvePartPath("xl/workbook.xml", target));
    });

    return Array.from(workbookXml.matchAll(/<sheet\b([^>]*)\/?>/g)).map((match) => {
      const attrs = match[1];
      const relationshipId = xmlAttributeValue(attrs, "r:id");
      return {
        displayName: xmlUnescape(xmlAttributeValue(attrs, "name")),
        sheetName: relationshipTargets.get(relationshipId) || ""
      };
    }).filter((entry) => entry.sheetName);
  }

  function worksheetFileNames(files) {
    return Object.keys(files)
      .filter((name) => /^xl\/worksheets\/sheet\d+\.xml$/.test(name))
      .sort((a, b) => Number(a.match(/sheet(\d+)\.xml/)?.[1] || 0) - Number(b.match(/sheet(\d+)\.xml/)?.[1] || 0));
  }

  function variantSkuHeaderRowIndex(rowRecords) {
    return rowRecords.slice(0, 20).findIndex((record) => {
      return (record.values || []).some((header) => normalizeHeader(header) === "variantsku");
    });
  }

  function hasVariantSkuHeader(rowRecords) {
    return variantSkuHeaderRowIndex(rowRecords) >= 0;
  }

  async function readXlsxWorkbook(fileOrBuffer) {
    const arrayBuffer = fileOrBuffer instanceof ArrayBuffer ? fileOrBuffer : await fileOrBuffer.arrayBuffer();
    const files = await unzipXlsx(arrayBuffer);
    const sharedStrings = parseSharedStrings(readFileText(files, "xl/sharedStrings.xml"));
    const sheetEntries = workbookSheetEntries(files);
    const sheetNames = sheetEntries.length
      ? sheetEntries.map((entry) => entry.sheetName).filter((name) => files[name])
      : worksheetFileNames(files);
    if (!sheetNames.length) throw new Error("No worksheet found.");

    const parsedSheets = sheetNames.map((sheetName) => ({
      sheetName,
      rowRecords: parseSheetRowRecords(readFileText(files, sheetName), sharedStrings),
      displayName: sheetEntries.find((entry) => entry.sheetName === sheetName)?.displayName || ""
    }));
    const selectedSheet = parsedSheets.find((sheet) => hasVariantSkuHeader(sheet.rowRecords))
      || parsedSheets.find((sheet) => normalizeHeader(sheet.displayName) === "variantsku")
      || parsedSheets[0];
    const headerRowIndex = Math.max(0, variantSkuHeaderRowIndex(selectedSheet.rowRecords));
    const rowRecords = selectedSheet.rowRecords.slice(headerRowIndex);

    return {
      files,
      sheetName: selectedSheet.sheetName,
      rowRecords,
      rows: rowRecords.map((record) => record.values)
    };
  }

  async function readXlsxRows(fileOrBuffer) {
    return (await readXlsxWorkbook(fileOrBuffer)).rows;
  }

  function buildStoredZip(fileMap) {
    const localParts = [];
    const centralParts = [];
    let offset = 0;

    Object.entries(fileMap).forEach(([name, content]) => {
      const nameBytes = textEncoder.encode(name);
      const contentBytes = typeof content === "string"
        ? textEncoder.encode(content)
        : content instanceof Uint8Array
          ? content
          : new Uint8Array(content || 0);
      const crc = crc32(contentBytes);
      const local = new Uint8Array(30 + nameBytes.length + contentBytes.length);

      writeUint32(local, 0, 0x04034b50);
      writeUint16(local, 4, 20);
      writeUint16(local, 6, 0);
      writeUint16(local, 8, 0);
      writeUint32(local, 14, crc);
      writeUint32(local, 18, contentBytes.length);
      writeUint32(local, 22, contentBytes.length);
      writeUint16(local, 26, nameBytes.length);
      local.set(nameBytes, 30);
      local.set(contentBytes, 30 + nameBytes.length);
      localParts.push(local);

      const central = new Uint8Array(46 + nameBytes.length);
      writeUint32(central, 0, 0x02014b50);
      writeUint16(central, 4, 20);
      writeUint16(central, 6, 20);
      writeUint16(central, 8, 0);
      writeUint16(central, 10, 0);
      writeUint32(central, 16, crc);
      writeUint32(central, 20, contentBytes.length);
      writeUint32(central, 24, contentBytes.length);
      writeUint16(central, 28, nameBytes.length);
      writeUint32(central, 42, offset);
      central.set(nameBytes, 46);
      centralParts.push(central);

      offset += local.length;
    });

    const centralOffset = offset;
    const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
    const eocd = new Uint8Array(22);
    writeUint32(eocd, 0, 0x06054b50);
    writeUint16(eocd, 8, centralParts.length);
    writeUint16(eocd, 10, centralParts.length);
    writeUint32(eocd, 12, centralSize);
    writeUint32(eocd, 16, centralOffset);

    return new Blob(localParts.concat(centralParts, eocd), {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
  }

  function makeXlsxBlob(rows) {
    const safeRows = rows.length ? rows : [[""]];
    const maxCols = Math.max(...safeRows.map((row) => row.length));
    const sheetRows = safeRows.map((row, rowIndex) => {
      const cells = Array.from({ length: maxCols }, (_, colIndex) => {
        const ref = `${colName(colIndex)}${rowIndex + 1}`;
        return `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(row[colIndex] || "")}</t></is></c>`;
      }).join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    }).join("");

    const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRows}</sheetData></worksheet>`;

    return buildStoredZip({
      "[Content_Types].xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`,
      "_rels/.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
      "xl/workbook.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Products" sheetId="1" r:id="rId1"/></sheets></workbook>`,
      "xl/_rels/workbook.xml.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`,
      "xl/worksheets/sheet1.xml": sheetXml
    });
  }

  function shiftRowXml(rowXml, newRowNumber, oldRowNumber) {
    let shiftedXml = rowXml
      .replace(/(<row\b[^>]*\br=")\d+(")/, `$1${newRowNumber}$2`)
      .replace(/(<c\b[^>]*\br="[A-Z]+)\d+(")/g, `$1${newRowNumber}$2`);

    if (oldRowNumber) {
      shiftedXml = shiftedXml.replace(
        new RegExp(`(\\$?[A-Z]{1,3}\\$?)${oldRowNumber}(\\b)`, "g"),
        `$1${newRowNumber}$2`
      );
    }

    return shiftedXml;
  }

  function getRowRange(rowXml) {
    const cellRefs = Array.from(rowXml.matchAll(/<c\b[^>]*\br="([A-Z]+)\d+"/g)).map((match) => match[1]);
    if (!cellRefs.length) return { firstCol: "A", lastCol: "A" };

    return {
      firstCol: cellRefs[0],
      lastCol: cellRefs[cellRefs.length - 1]
    };
  }

  function getMatchedMergeCells(sheetXml, rowNumberMap) {
    const mergeBlock = sheetXml.match(/<mergeCells\b[^>]*>[\s\S]*?<\/mergeCells>/)?.[0] || "";
    const mergeCells = [];

    Array.from(mergeBlock.matchAll(/<mergeCell\b[^>]*\bref="([A-Z]+)(\d+):([A-Z]+)(\d+)"[^/]*\/>/g)).forEach((match) => {
      const startCol = match[1];
      const startRow = parseInt(match[2], 10);
      const endCol = match[3];
      const endRow = parseInt(match[4], 10);

      const mappedRows = [];
      for (let row = startRow; row <= endRow; row += 1) {
        const mappedRow = rowNumberMap.get(row);
        if (!mappedRow) return;
        mappedRows.push(mappedRow);
      }
      if (mappedRows.some((row, index) => index && row !== mappedRows[index - 1] + 1)) return;
      mergeCells.push(
        `<mergeCell ref="${startCol}${mappedRows[0]}:${endCol}${mappedRows[mappedRows.length - 1]}"/>`
      );
    });

    return mergeCells;
  }

  function stripWorksheetPartsForMatchedExport(sheetXml) {
    return sheetXml
      .replace(/<mergeCells\b[^>]*>[\s\S]*?<\/mergeCells>/, "")
      .replace(/<autoFilter\b[^>]*>[\s\S]*?<\/autoFilter>|<autoFilter\b[^>]*\/>/, "")
      .replace(/<hyperlinks\b[^>]*>[\s\S]*?<\/hyperlinks>/, "")
      .replace(/<dimension\b[^>]*\/>/, "");
  }

  function getMatchedHyperlinks(sheetXml, rowNumberMap) {
    const hyperlinkBlock = sheetXml.match(/<hyperlinks\b[^>]*>[\s\S]*?<\/hyperlinks>/)?.[0] || "";
    const links = [];

    Array.from(hyperlinkBlock.matchAll(/<hyperlink\b[^>]*\bref="([A-Z]+)(\d+)"[^>]*\/>/g)).forEach((match) => {
      const newRow = rowNumberMap.get(parseInt(match[2], 10));
      if (!newRow) return;
      links.push(match[0].replace(`ref="${match[1]}${match[2]}"`, `ref="${match[1]}${newRow}"`));
    });

    return links.length ? `<hyperlinks>${links.join("")}</hyperlinks>` : "";
  }

  function clearCellValue(rowXml, column, rowNumber) {
    const cellPattern = new RegExp(`<c\\b([^>]*\\br="${column}${rowNumber}"[^>]*)>[\\s\\S]*?<\\/c>`);
    return rowXml.replace(cellPattern, (_, attrs) => {
      const cleanAttrs = attrs.replace(/\s+t="[^"]*"/g, "").replace(/\s+vm="[^"]*"/g, "");
      return `<c${cleanAttrs} t="inlineStr"><is><t></t></is></c>`;
    });
  }

  function getRowHeightPoints(rowXml, fallback = 30) {
    return parseFloat(rowXml.match(/<row\b[^>]*\bht="([^"]+)"/)?.[1] || fallback);
  }

  function rowHeightPixels(rowXml, fallback = 30) {
    return getRowHeightPoints(rowXml, fallback) * 96 / 72;
  }

  function setRowHeightPixels(rowXml, heightPixels) {
    const heightPoints = Math.round(heightPixels * 72 / 96 * 100) / 100;
    if (/<row\b[^>]*\bht="[^"]*"/.test(rowXml)) {
      return rowXml
        .replace(/(<row\b[^>]*\bht=")[^"]*(")/, `$1${heightPoints}$2`)
        .replace(/(<row\b(?![^>]*\bcustomHeight=)[^>]*)(>)/, '$1 customHeight="1"$2');
    }
    return rowXml.replace(/<row\b([^>]*)>/, `<row$1 ht="${heightPoints}" customHeight="1">`);
  }

  function ensureMinimumImageRowHeight(rowXml) {
    return rowHeightPixels(rowXml) <= 190 ? setRowHeightPixels(rowXml, 191) : rowXml;
  }

  function getColumnWidth(sheetXml, columnIndex) {
    const oneBasedIndex = columnIndex + 1;
    const columns = Array.from(sheetXml.matchAll(/<col\b([^>]*)\/>/g));
    const match = columns.find((column) => {
      const min = parseInt(column[1].match(/\bmin="(\d+)"/)?.[1] || "0", 10);
      const max = parseInt(column[1].match(/\bmax="(\d+)"/)?.[1] || "0", 10);
      return oneBasedIndex >= min && oneBasedIndex <= max;
    });
    return parseFloat(match?.[1].match(/\bwidth="([^"]+)"/)?.[1] || "8.43");
  }

  function imageExtension(contentType, url) {
    const type = String(contentType || "").toLowerCase();
    if (type.includes("png")) return "png";
    if (type.includes("gif")) return "gif";
    if (type.includes("webp")) return "webp";
    if (type.includes("jpeg") || type.includes("jpg")) return "jpeg";
    const urlExtension = String(url || "").match(/\.(png|gif|webp|jpe?g)(?:[?#]|$)/i)?.[1]?.toLowerCase();
    return urlExtension === "jpg" ? "jpeg" : urlExtension || "jpeg";
  }

  async function getImageSize(blob) {
    if (window.createImageBitmap) {
      try {
        const bitmap = await createImageBitmap(blob);
        const size = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
        return size;
      } catch (error) {
        // Fall through to the image element for browsers with partial bitmap support.
      }
    }

    return new Promise((resolve) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(blob);
      image.onload = () => {
        resolve({ width: image.naturalWidth || 1, height: image.naturalHeight || 1 });
        URL.revokeObjectURL(objectUrl);
      };
      image.onerror = () => {
        resolve({ width: 1, height: 1 });
        URL.revokeObjectURL(objectUrl);
      };
      image.src = objectUrl;
    });
  }

  async function fetchEmbeddedImage(url, newRowNumber) {
    try {
      const response = await fetch(url, { mode: "cors", credentials: "omit" });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) return null;
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const size = await getImageSize(blob);
      return {
        bytes,
        extension: imageExtension(blob.type, url),
        mimeType: blob.type,
        newRowNumber,
        width: size.width,
        height: size.height
      };
    } catch (error) {
      return null;
    }
  }

  async function mapWithConcurrency(items, limit, mapper) {
    const results = new Array(items.length);
    let nextIndex = 0;

    async function worker() {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await mapper(items[index], index);
      }
    }

    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
  }

  function nextRelationshipId(relationshipsXml) {
    const ids = Array.from(relationshipsXml.matchAll(/\bId="rId(\d+)"/g)).map((match) => parseInt(match[1], 10));
    return `rId${Math.max(0, ...ids) + 1}`;
  }

  function ensureContentType(contentTypesXml, extension, contentType) {
    const hasExtension = new RegExp(`<Default\\b[^>]*Extension="${extension}"`, "i").test(contentTypesXml);
    if (hasExtension) return contentTypesXml;
    return contentTypesXml.replace(
      "</Types>",
      `<Default Extension="${extension}" ContentType="${contentType}"/></Types>`
    );
  }

  function resolvePartPath(basePart, target) {
    const parts = basePart.split("/");
    parts.pop();
    String(target || "").split("/").forEach((part) => {
      if (!part || part === ".") return;
      if (part === "..") parts.pop();
      else parts.push(part);
    });
    return parts.join("/");
  }

  function drawingRelationshipsName(drawingName) {
    const parts = drawingName.split("/");
    const fileName = parts.pop();
    return `${parts.join("/")}/_rels/${fileName}.rels`;
  }

  function getColumnStartPixels(sheetXml, columnIndex) {
    let pixels = 0;
    for (let index = 0; index < columnIndex; index += 1) {
      pixels += Math.max(1, Math.floor(getColumnWidth(sheetXml, index) * 7 + 5));
    }
    return pixels;
  }

  function getRowStartPixels(outputRecords, rowNumber) {
    return outputRecords.slice(0, Math.max(0, rowNumber - 1)).reduce((total, record) => {
      return total + Math.max(1, rowHeightPixels(record?.rowXml));
    }, 0);
  }

  function addEmbeddedImageParts(outputFiles, workbook, sheetXml, imageAssets, imageColumnIndex, outputRecords) {
    if (!imageAssets.length) return sheetXml;

    const sheetFileName = workbook.sheetName.split("/").pop();
    const sheetRelsName = `xl/worksheets/_rels/${sheetFileName}.rels`;
    let sheetRelationships = readFileText(outputFiles, sheetRelsName);
    if (!sheetRelationships) {
      sheetRelationships = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';
    }
    const existingDrawingRelationshipId = sheetXml.match(/<drawing\b[^>]*\br:id="([^"]+)"[^>]*\/>/)?.[1];
    const existingDrawingRelationship = existingDrawingRelationshipId
      ? Array.from(sheetRelationships.matchAll(/<Relationship\b[^>]*\/>/g)).find((match) => {
        return new RegExp(`\\bId="${existingDrawingRelationshipId}"`).test(match[0]);
      })?.[0]
      : "";
    const existingDrawingTarget = existingDrawingRelationship?.match(/\bTarget="([^"]+)"/)?.[1];
    const existingDrawingName = existingDrawingTarget
      ? resolvePartPath(workbook.sheetName, existingDrawingTarget)
      : "";
    const drawingNumbers = Object.keys(outputFiles)
      .map((name) => parseInt(name.match(/^xl\/drawings\/drawing(\d+)\.xml$/)?.[1] || "0", 10));
    const drawingNumber = existingDrawingName
      ? parseInt(existingDrawingName.match(/drawing(\d+)\.xml$/)?.[1] || "1", 10)
      : Math.max(0, ...drawingNumbers) + 1;
    const drawingName = existingDrawingName || `xl/drawings/drawing${drawingNumber}.xml`;
    const drawingRelsName = drawingRelationshipsName(drawingName);
    const drawingRelationshipId = existingDrawingRelationshipId || nextRelationshipId(sheetRelationships);

    if (!existingDrawingRelationshipId) {
      sheetRelationships = sheetRelationships.replace(
        "</Relationships>",
        `<Relationship Id="${drawingRelationshipId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${drawingNumber}.xml"/></Relationships>`
      );
    }
    outputFiles[sheetRelsName] = sheetRelationships;

    const columnWidthPixels = Math.max(40, Math.floor(getColumnWidth(sheetXml, imageColumnIndex) * 7 + 5));
    const columnStartPixels = getColumnStartPixels(sheetXml, imageColumnIndex);
    const anchors = [];
    let drawingXml = readFileText(outputFiles, drawingName);
    if (!drawingXml) {
      drawingXml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"></xdr:wsDr>';
    }
    let drawingRelationships = readFileText(outputFiles, drawingRelsName);
    if (!drawingRelationships) {
      drawingRelationships = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';
    }
    let nextDrawingRelationshipNumber = parseInt(nextRelationshipId(drawingRelationships).replace("rId", ""), 10);
    let nextPictureId = Math.max(
      0,
      ...Array.from(drawingXml.matchAll(/<xdr:cNvPr\b[^>]*\bid="(\d+)"/g)).map((match) => parseInt(match[1], 10))
    ) + 1;
    let nextMediaNumber = Math.max(
      0,
      ...Object.keys(outputFiles).map((name) => parseInt(name.match(/^xl\/media\/image(\d+)\./)?.[1] || "0", 10))
    ) + 1;

    imageAssets.forEach((asset) => {
      const pictureId = nextPictureId;
      const relationshipId = `rId${nextDrawingRelationshipNumber}`;
      const mediaName = `image${nextMediaNumber}.${asset.extension}`;
      nextPictureId += 1;
      nextDrawingRelationshipNumber += 1;
      nextMediaNumber += 1;
      const rowRecord = outputRecords[asset.newRowNumber - 1];
      const currentRowHeightPixels = Math.max(20, rowHeightPixels(rowRecord?.rowXml));
      const width = 180;
      const height = 180;
      const columnOffsetPixels = Math.max(0, Math.round((columnWidthPixels - width) / 2));
      const rowOffsetPixels = Math.max(0, Math.round((currentRowHeightPixels - height) / 2));
      const columnOffset = columnOffsetPixels * 9525;
      const rowOffset = rowOffsetPixels * 9525;
      const widthEmu = width * 9525;
      const heightEmu = height * 9525;
      const absoluteX = (columnStartPixels + columnOffsetPixels) * 9525;
      const absoluteY = (getRowStartPixels(outputRecords, asset.newRowNumber) + rowOffsetPixels) * 9525;

      outputFiles[`xl/media/${mediaName}`] = asset.bytes;
      drawingRelationships = drawingRelationships.replace(
        "</Relationships>",
        `<Relationship Id="${relationshipId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${mediaName}"/>`
          + "</Relationships>"
      );
      anchors.push(
        `<xdr:twoCellAnchor editAs="oneCell"><xdr:from><xdr:col>${imageColumnIndex}</xdr:col><xdr:colOff>${columnOffset}</xdr:colOff><xdr:row>${asset.newRowNumber - 1}</xdr:row><xdr:rowOff>${rowOffset}</xdr:rowOff></xdr:from><xdr:to><xdr:col>${imageColumnIndex}</xdr:col><xdr:colOff>${columnOffset + widthEmu}</xdr:colOff><xdr:row>${asset.newRowNumber - 1}</xdr:row><xdr:rowOff>${rowOffset + heightEmu}</xdr:rowOff></xdr:to><xdr:pic><xdr:nvPicPr><xdr:cNvPr id="${pictureId}" name="Image1 ${pictureId}"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr><xdr:blipFill><a:blip r:embed="${relationshipId}"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill><xdr:spPr><a:xfrm><a:off x="${absoluteX}" y="${absoluteY}"/><a:ext cx="${widthEmu}" cy="${heightEmu}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic><xdr:clientData/></xdr:twoCellAnchor>`
      );
    });

    outputFiles[drawingName] = drawingXml.includes("</xdr:wsDr>")
      ? drawingXml.replace("</xdr:wsDr>", `${anchors.join("")}</xdr:wsDr>`)
      : drawingXml.replace(/<xdr:wsDr\b([^>]*)\/>/, `<xdr:wsDr$1>${anchors.join("")}</xdr:wsDr>`);
    outputFiles[drawingRelsName] = drawingRelationships;

    let contentTypes = readFileText(outputFiles, "[Content_Types].xml");
    if (!new RegExp(`PartName="/${drawingName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}"`).test(contentTypes)) {
      contentTypes = contentTypes.replace(
        "</Types>",
        `<Override PartName="/${drawingName}" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>`
      );
    }
    imageAssets.forEach((asset) => {
      const mimeTypes = {
        gif: "image/gif",
        jpeg: "image/jpeg",
        png: "image/png",
        webp: "image/webp"
      };
      contentTypes = ensureContentType(contentTypes, asset.extension, mimeTypes[asset.extension] || asset.mimeType);
    });
    outputFiles["[Content_Types].xml"] = contentTypes;

    return existingDrawingRelationshipId
      ? sheetXml
      : sheetXml.replace("</worksheet>", `<drawing r:id="${drawingRelationshipId}"/></worksheet>`);
  }

  function prepareWorkbookFilesForMatchedExport(files) {
    const outputFiles = { ...files };

    delete outputFiles["xl/calcChain.xml"];

    if (outputFiles["[Content_Types].xml"]) {
      outputFiles["[Content_Types].xml"] = readFileText(outputFiles, "[Content_Types].xml")
        .replace(/<Override\b[^>]*PartName="\/xl\/calcChain\.xml"[^>]*\/>/, "");
    }

    if (outputFiles["xl/_rels/workbook.xml.rels"]) {
      outputFiles["xl/_rels/workbook.xml.rels"] = readFileText(outputFiles, "xl/_rels/workbook.xml.rels")
        .replace(/<Relationship\b[^>]*Type="http:\/\/schemas\.openxmlformats\.org\/officeDocument\/2006\/relationships\/calcChain"[^>]*\/>/, "");
    }

    if (outputFiles["xl/workbook.xml"]) {
      const workbookXml = readFileText(outputFiles, "xl/workbook.xml");
      if (/<calcPr\b[^>]*\/>/.test(workbookXml)) {
        outputFiles["xl/workbook.xml"] = workbookXml
          .replace(/<calcPr\b[^>]*\/>/, '<calcPr calcMode="auto" fullCalcOnLoad="1" forceFullCalc="1"/>');
      } else {
        outputFiles["xl/workbook.xml"] = workbookXml
          .replace("</workbook>", '<calcPr calcMode="auto" fullCalcOnLoad="1" forceFullCalc="1"/></workbook>');
      }
    }

    return outputFiles;
  }

  async function makeMatchedWorkbookBlobFromSource(workbook, records) {
    let outputRecords = records.filter(Boolean);
    if (!outputRecords.length) return makeXlsxBlob([[""]]);
    const outputFiles = prepareWorkbookFilesForMatchedExport(workbook.files);
    const sourceSheetXml = readFileText(outputFiles, workbook.sheetName);
    const imageColumns = findImageColumnIndexes(outputRecords[0].values);
    const imageSourceColumnIndex = imageColumns.sourceIndex;
    const imageColumnIndex = imageColumns.displayIndex;
    const imageRequests = imageSourceColumnIndex < 0 || imageColumnIndex < 0 ? [] : outputRecords.slice(1).map((record, index) => ({
      newRowNumber: index + 2,
      url: splitImageUrls(record.values[imageSourceColumnIndex])[0]
    })).filter((item) => item.url);
    const imageAssets = (await mapWithConcurrency(
      imageRequests,
      4,
      (item) => fetchEmbeddedImage(item.url, item.newRowNumber)
    )).filter(Boolean);
    const rowsWithEmbeddedImages = new Set(imageAssets.map((asset) => asset.newRowNumber));

    outputRecords = outputRecords.map((record, index) => {
      const nextRowNumber = index + 1;
      return rowsWithEmbeddedImages.has(nextRowNumber)
        ? { ...record, rowXml: ensureMinimumImageRowHeight(record.rowXml) }
        : record;
    });

    const rowNumberMap = new Map();
    const sheetRows = outputRecords.map((record, index) => {
      const nextRowNumber = index + 1;
      if (record.rowNumber) rowNumberMap.set(record.rowNumber, nextRowNumber);
      const shiftedRow = shiftRowXml(record.rowXml, nextRowNumber, record.rowNumber);
      return imageColumnIndex >= 0 && rowsWithEmbeddedImages.has(nextRowNumber)
        ? clearCellValue(shiftedRow, colName(imageColumnIndex), nextRowNumber)
        : shiftedRow;
    }).join("");
    const range = getRowRange(outputRecords[0].rowXml);
    const dimensionRef = `${range.firstCol}1:${range.lastCol}${outputRecords.length}`;
    const mergeCells = getMatchedMergeCells(sourceSheetXml, rowNumberMap);
    const mergeXml = mergeCells.length ? `<mergeCells count="${mergeCells.length}">${mergeCells.join("")}</mergeCells>` : "";
    const autoFilterXml = /<autoFilter\b/.test(sourceSheetXml) ? `<autoFilter ref="${dimensionRef}"/>` : "";
    const hyperlinkXml = getMatchedHyperlinks(sourceSheetXml, rowNumberMap);
    let sheetXml = stripWorksheetPartsForMatchedExport(sourceSheetXml)
      .replace(/<worksheet\b([^>]*)>/, `<worksheet$1><dimension ref="${dimensionRef}"/>`)
      .replace(
        /<sheetData\b[^>]*>[\s\S]*?<\/sheetData>/,
        `<sheetData>${sheetRows}</sheetData>${autoFilterXml}${mergeXml}${hyperlinkXml}`
      );

    sheetXml = addEmbeddedImageParts(
      outputFiles,
      workbook,
      sheetXml,
      imageAssets,
      imageColumnIndex,
      outputRecords
    );

    const workbookXml = readFileText(outputFiles, "xl/workbook.xml");
    outputFiles["xl/workbook.xml"] = workbookXml.replace(
      /(<definedName\b[^>]*name="_xlnm\._FilterDatabase"[^>]*>)([\s\S]*?)(<\/definedName>)/,
      (_, open, value, close) => {
        const sheetPrefix = value.includes("!") ? value.slice(0, value.indexOf("!") + 1) : "";
        return `${open}${sheetPrefix}$${range.firstCol}$1:$${range.lastCol}$${outputRecords.length}${close}`;
      }
    );

    return buildStoredZip({
      ...outputFiles,
      [workbook.sheetName]: sheetXml
    });
  }

  function downloadBlob(filename, blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function downloadUrl(url, index) {
    window.setTimeout(() => {
      const link = document.createElement("a");
      link.href = url;
      link.download = "";
      link.target = "_blank";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }, index * 250);
  }

  function padImageIndex(index) {
    return index < 9 ? `0${index + 1}` : String(index + 1);
  }

  function splitImageUrls(value) {
    const normalizedValue = String(value || "")
      .replace(/&amp;/g, "&")
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/\\u0026/gi, "&")
      .replace(/\\\//g, "/");
    const urls = normalizedValue.match(/(?:https?:)?\/\/[^\s"'<>()[\]{},]+/gi) || [];
    const seen = new Set();

    return urls
      .map(normalizeImageDownloadUrl)
      .filter((url) => {
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      });
  }

  function shouldShowDownloadLocationHint() {
    return window.matchMedia("(max-width: 750px)").matches;
  }

  function normalizeImageDownloadUrl(url) {
    let value = String(url || "").trim().replace(/[;,.]+$/g, "");
    if (!value) return "";
    if (value.startsWith("//")) value = `https:${value}`;

    try {
      const parsedUrl = new URL(value, window.location.origin);
      if (!/^https?:$/i.test(parsedUrl.protocol)) return "";

      parsedUrl.pathname = parsedUrl.pathname.replace(
        /_(?:pico|icon|thumb|small|compact|medium|large|grande|master|\d+x\d*|x\d+)(\.(?:jpe?g|png|webp|gif|avif))$/i,
        "$1"
      );
      ["width", "height", "crop"].forEach((parameter) => parsedUrl.searchParams.delete(parameter));
      return parsedUrl.toString();
    } catch (error) {
      return /^https?:\/\//i.test(value) ? value : "";
    }
  }

  function imageItemsFromUrls(value, sku) {
    const safeSku = String(sku || "sku").trim() || "sku";
    return splitImageUrls(value).map((url, index) => ({
      url,
      folder: safeSku,
      filename: `${safeSku}_${padImageIndex(index)}`
    }));
  }

  function getImageDownloadIcon(sourceButton) {
    return sourceButton?.querySelector(".rs-download-csv__type-icon, .rs-download-sku-toolbar__icon");
  }

  function getImageDownloadMessage(sourceButton) {
    let message = sourceButton?.querySelector("[data-rs-bulk-download-message]");
    if (message || !sourceButton) return message;

    message = document.createElement("span");
    message.className = "rs-image-download-message";
    message.setAttribute("data-rs-bulk-download-message", "");
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");
    message.hidden = true;
    sourceButton.appendChild(message);
    return message;
  }

  function hideImageDownloadMessage(sourceButton) {
    const message = sourceButton?.querySelector("[data-rs-bulk-download-message]");
    const timer = imageDownloadMessageTimers.get(sourceButton);

    if (timer) {
      window.clearTimeout(timer);
      imageDownloadMessageTimers.delete(sourceButton);
    }

    if (!message) return;
    message.classList.remove("is-visible");
    message.hidden = true;
  }

  function showImageDownloadMessage(sourceButton, locationMessage = "Find the ZIP in your browser Downloads.") {
    const message = getImageDownloadMessage(sourceButton);
    if (!message) return;

    hideImageDownloadMessage(sourceButton);
    message.innerHTML = shouldShowDownloadLocationHint() && locationMessage
      ? `${SUCCESS_ICON_SVG}<span>Download complete.<br>${locationMessage}</span>`
      : `${SUCCESS_ICON_SVG}<span>Download complete.</span>`;
    message.hidden = false;
    message.classList.add("is-visible");
    imageDownloadMessageTimers.set(sourceButton, window.setTimeout(() => {
      message.classList.remove("is-visible");
      message.hidden = true;
      imageDownloadMessageTimers.delete(sourceButton);
    }, 2000));
  }

  function setImageDownloadState(sourceButton, state, locationMessage) {
    if (!sourceButton) return;

    const icon = getImageDownloadIcon(sourceButton);
    const isLoading = state === "loading";

    sourceButton.classList.toggle("is-loading", isLoading);
    sourceButton.disabled = isLoading;
    sourceButton.setAttribute("aria-busy", isLoading ? "true" : "false");
    sourceButton.setAttribute("aria-disabled", isLoading ? "true" : "false");

    if (state === "complete") {
      showImageDownloadMessage(sourceButton, locationMessage);
    } else if (state !== "loading") {
      hideImageDownloadMessage(sourceButton);
    }
  }

  function triggerImageDownload(images, productTitle = "Rich Solar Images", productHandle = "rich-solar-images", sourceButton = null) {
    const validImages = (images || []).filter((image) => image?.url);
    if (!validImages.length) return false;

    const wrapper = document.createElement("span");
    const button = document.createElement("button");
    let settled = false;

    wrapper.className = "rs-image-download-ui";
    wrapper.hidden = true;
    button.type = "button";
    button.className = "rs-image-zip-btn";
    button.dataset.productTitle = productTitle;
    button.dataset.productHandle = productHandle;
    button.dataset.images = JSON.stringify(validImages);
    button.addEventListener("rs:image-zip:state", (event) => {
      const state = event.detail?.state || "idle";
      setImageDownloadState(sourceButton, state, event.detail?.locationMessage);
      if (["complete", "idle", "error"].includes(state)) {
        settled = true;
        window.setTimeout(() => wrapper.remove(), 100);
      }
    });
    wrapper.appendChild(button);
    document.body.appendChild(wrapper);
    setImageDownloadState(sourceButton, "loading");
    button.click();
    window.setTimeout(() => {
      if (!settled) setImageDownloadState(sourceButton, "idle");
      wrapper.remove();
    }, 300000);
    return true;
  }

  function normalizeHeader(value) {
    return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
  }

  function findColumnIndex(headers, candidates) {
    const normalized = headers.map(normalizeHeader);
    return normalized.findIndex((header) => candidates.includes(header));
  }

  function splitSkuValues(value) {
    return String(value || "")
      .split(/[\r\n,;|]+/)
      .map((sku) => sku.trim())
      .filter(Boolean);
  }

  function findImageColumnIndexes(headers) {
    const normalizedHeaders = headers.map(normalizeHeader);
    const sourceCandidates = [
      "image1link",
      "imagelink",
      "image1src",
      "imagesrc",
      "imageurl",
      "image1url",
      "image"
    ];
    const displayCandidates = [
      "image1link",
      "imagelink",
      "image",
      "imagesrc",
      "imageurl",
      "image1src",
      "image1url"
    ];
    const findCandidate = (candidates) => normalizedHeaders.findIndex((header) => candidates.includes(header));

    return {
      sourceIndex: findCandidate(sourceCandidates),
      displayIndex: findCandidate(displayCandidates)
    };
  }

  function findDownloadImagesColumnIndex(headers) {
    const normalizedHeaders = headers.map(normalizeHeader);
    const candidates = [
      "imagesrc",
      "image1src",
      "imageurl",
      "image1url",
      "imagelink",
      "image1link",
      "image"
    ];

    for (const candidate of candidates) {
      const index = normalizedHeaders.indexOf(candidate);
      if (index >= 0) return index;
    }

    return -1;
  }

  function parseCsvRows(text) {
    const rows = [];
    let row = [];
    let value = "";
    let quoted = false;

    for (let index = 0; index < text.length; index += 1) {
      const char = text[index];
      const next = text[index + 1];
      if (quoted && char === '"' && next === '"') {
        value += '"';
        index += 1;
      } else if (char === '"') {
        quoted = !quoted;
      } else if (!quoted && char === ",") {
        row.push(value.trim());
        value = "";
      } else if (!quoted && (char === "\n" || char === "\r")) {
        if (char === "\r" && next === "\n") index += 1;
        row.push(value.trim());
        if (row.some(Boolean)) rows.push(row);
        row = [];
        value = "";
      } else {
        value += char;
      }
    }

    row.push(value.trim());
    if (row.some(Boolean)) rows.push(row);
    return rows;
  }

  function extractSkusFromRows(rows, options = {}) {
    if (!rows.length) return [];
    const skuColumn = findColumnIndex(rows[0], ["variantsku", "sku"]);
    const dataRows = options.skipFirstRow || skuColumn >= 0 ? rows.slice(1) : rows;
    return dataRows.map((row) => (skuColumn >= 0 ? row[skuColumn] : row[0])).map((sku) => String(sku || "").trim()).filter(Boolean);
  }

  function downloadSampleSkuTemplate() {
    downloadBlob(
      "SKU List Template.csv",
      new Blob([
        "Sku\r\n",
        "RS-PWM10WP\r\n",
        "RS-PWM20WP\r\n",
        "RS-PWM30F\r\n",
        "RS-PWM30P\r\n",
        "RS-PWM30PF\r\n",
        "RS-PWM30WP\r\n",
        "RS-PWM40P\r\n",
        "RS-PWM8WP\r\n"
      ], { type: "text/csv;charset=utf-8" })
    );
  }

  function normalizeProductListUrl(url) {
    const value = String(url || "").trim();
    if (!value) return "";

    try {
      const parsedUrl = new URL(value, window.location.origin);
      const sheetsMatch = parsedUrl.pathname.match(/\/spreadsheets\/d\/([^/]+)/);
      if (parsedUrl.hostname === "docs.google.com" && sheetsMatch?.[1]) {
        return `https://docs.google.com/spreadsheets/d/${sheetsMatch[1]}/export?format=xlsx`;
      }

      const driveMatch = parsedUrl.pathname.match(/\/file\/d\/([^/]+)/);
      if (parsedUrl.hostname === "drive.google.com" && driveMatch?.[1]) {
        return `https://drive.google.com/uc?export=download&id=${driveMatch[1]}`;
      }
    } catch (error) {
      return value;
    }

    return value;
  }

  function showCsvError(error) {
    console.error(error);
    window.alert(error?.message || "Unable to process the uploaded SKU file.");
  }

  function isZipArrayBuffer(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer.slice(0, 4));
    return bytes[0] === 0x50 && bytes[1] === 0x4b;
  }

  async function getProductWorkbook(productListUrl) {
    if (!productListUrl) {
      throw new Error("Product list XLSX URL is not configured.");
    }

    if (!productListPromises.has(productListUrl)) {
      productListPromises.set(productListUrl, fetch(productListUrl)
        .then((response) => {
          if (!response.ok) throw new Error("Unable to load product list XLSX.");
          return response;
        })
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => {
          if (!isZipArrayBuffer(arrayBuffer)) {
            productListPromises.delete(productListUrl);
            throw new Error("Product list is still syncing from Google Drive. Please try again in a moment.");
          }

          return arrayBuffer;
        })
        .then(readXlsxWorkbook));
    }

    return productListPromises.get(productListUrl);
  }

  async function getProductListRows(productListUrl) {
    return (await getProductWorkbook(productListUrl)).rows;
  }

  async function matchProductRowsBySku(productListUrl, skus) {
    const workbook = await getProductWorkbook(productListUrl);
    const headerRecord = workbook.rowRecords[0];
    const headers = headerRecord?.values || [];
    const rowRecords = workbook.rowRecords.slice(1);
    const skuIndexes = headers
      .map((header, index) => ({ header: normalizeHeader(header), index }))
      .filter((item) => item.header === "variantsku")
      .map((item) => item.index);

    if (!skuIndexes.length) {
      throw new Error("Variant SKU column was not found in all-product-list.xlsx.");
    }

    const rowBySku = new Map();
    rowRecords.forEach((record) => {
      skuIndexes.forEach((skuIndex) => {
        const row = record.values;
        splitSkuValues(row[skuIndex]).forEach((sku) => {
          const key = sku.toLowerCase();
          if (key && !rowBySku.has(key)) rowBySku.set(key, record);
        });
      });
    });

    const seen = new Set();
    const seenRecords = new Set();
    const foundSkus = [];
    const missingSkus = [];
    const matchedRows = [];
    const matchedRowRecords = [];

    (skus || []).forEach((sku) => {
      const key = String(sku || "").trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      const record = rowBySku.get(key);
      if (record) {
        foundSkus.push(sku);
        // Multiple variant SKUs can point to the same product feed row.
        if (!seenRecords.has(record)) {
          seenRecords.add(record);
          matchedRows.push(record.values);
          matchedRowRecords.push(record);
        }
      } else {
        missingSkus.push(sku);
      }
    });

    return { workbook, headerRecord, headers, matchedRows, matchedRowRecords, missingSkus, foundSkus };
  }

  document.querySelectorAll("[data-rs-download-csv]").forEach((section) => {
    const tabs = section.querySelectorAll("[data-rs-download-tab]");
    const textarea = section.querySelector("[data-rs-csv-skus]");
    const applyButton = section.querySelector("[data-rs-csv-apply]");
    const sampleLink = section.querySelector("[data-rs-csv-sample]");
    const fileInput = section.querySelector("[data-rs-csv-file]");
    const summary = section.querySelector("[data-rs-csv-summary]");
    const totalEl = section.querySelector("[data-rs-csv-total]");
    const foundEl = section.querySelector("[data-rs-csv-found]");
    const missingEl = section.querySelector("[data-rs-csv-missing]");
    const foundElItem = section.querySelector(".rs-download-csv__summary-item--found");
    const boxStepHeader = section.querySelector(".rs-download-csv__step-header");
    const missingElItem = section.querySelector(".rs-download-csv__summary-item--missing");
    const viewDetails = section.querySelector("[data-rs-csv-view-details]");
    const productListUrl = normalizeProductListUrl(section.dataset.productListUrl);
    const tabTargets = Array.from(tabs)
      .map((tab) => document.getElementById(tab.dataset.id))
      .filter(Boolean);
    let parsedSkus = [];
    let missingSkus = [];
    let matchedRows = [];
    let matchedRowRecords = [];
    let matchedImageItems = [];
    let productHeaders = [];
    let productWorkbook = null;
    let productHeaderRecord = null;
    let csvFeedPending = false;

    function getSyncStatus() {
      let status = section.querySelector("[data-rs-csv-sync-status]");
      if (status) return status;

      status = document.createElement("p");
      status.className = "rs-download-csv__sync-status";
      status.setAttribute("data-rs-csv-sync-status", "");
      status.hidden = true;
      section.querySelector(".rs-download-csv__card--step-one")?.appendChild(status);
      return status;
    }

    function setCsvSyncState(state, message = "") {
      const isSyncing = state === "syncing";
      const status = getSyncStatus();

      if (applyButton) {
        applyButton.disabled = isSyncing;
        applyButton.classList.toggle("is-loading", isSyncing);
        applyButton.setAttribute("aria-busy", isSyncing ? "true" : "false");
      }

      status.textContent = message;
      status.hidden = !message;
      status.classList.toggle("is-error", state === "error");
      status.classList.toggle("is-syncing", isSyncing);
    }

    function centerActiveDownloadTab(tab, behavior = "smooth") {
      if (!tab) return;
      tab.scrollIntoView({ behavior, block: "nearest", inline: "center" });
    }

    function activateTab(tab) {
      const targetId = tab.dataset.id;
      if (!targetId || !document.getElementById(targetId)) return;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      tabTargets.forEach((target) => {
        target.hidden = target.id !== targetId;
      });

      centerActiveDownloadTab(tab);
    }

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        activateTab(tab);
      });
    });

    const activeTab = section.querySelector("[data-rs-download-tab].is-active") || tabs[0];
    if (activeTab) {
      activateTab(activeTab);
      requestAnimationFrame(() => centerActiveDownloadTab(activeTab, "auto"));
    }

    sampleLink?.addEventListener("click", (event) => {
      if (sampleLink.getAttribute("href")) return;
      event.preventDefault();
      downloadSampleSkuTemplate();
    });

    function parseSkuText(text) {
      return (text || "")
        .split(/[\s,;]+/)
        .map((sku) => sku.trim())
        .filter(Boolean);
    }

    function setUploadMode() {
      if (textarea) textarea.value = "";
    }

    function setTextMode() {
      if (!fileInput) return;
      fileInput.value = "";
      const fileName = section.querySelector("[data-rs-csv-file-name]");
      if (fileName) fileName.textContent = "Grag and drop your CSV file here";
    }

    async function applySkus() {
      setCsvSyncState("syncing", "Matching product list, please wait...");
      const result = await matchProductRowsBySku(productListUrl, parsedSkus);
      productWorkbook = result.workbook;
      productHeaderRecord = result.headerRecord;
      productHeaders = result.headers;
      const imageIndex = findDownloadImagesColumnIndex(result.headers);
      matchedRows = [];
      matchedRowRecords = [];
      matchedImageItems = [];
      missingSkus = result.missingSkus;
      matchedRows = result.matchedRows;
      matchedRowRecords = result.matchedRowRecords;

      matchedRows.forEach((row, index) => {
        const sku = result.foundSkus[index];
        const imageUrl = String(row[imageIndex] || "").trim();
        if (imageIndex >= 0 && imageUrl) {
          matchedImageItems.push(...imageItemsFromUrls(imageUrl, sku));
        }
      });

      // 更新总数
      if (totalEl) totalEl.textContent = parsedSkus.length;
      // 更新 found
      if (foundEl) {
        const count = result.foundSkus.length;
        foundElItem.style.display = count ? 'inline-flex' : 'none';
        foundEl.textContent = count;
      }
      // 更新 missing
      if (missingEl) {
        const count = missingSkus.length;
        missingElItem.style.display = count ? 'inline-flex' : 'none';
        missingEl.textContent = count;
      }
      // 更新 viewDetails
      if (viewDetails) {
        const shouldShow = missingSkus.length > 0 && result.foundSkus.length > 0;
        viewDetails.style.display = shouldShow ? 'block' : 'none';
        viewDetails.hidden = !shouldShow;
      }
      if (summary) summary.hidden = parsedSkus.length === 0;
      section.querySelector(".rs-download-csv__card--step-two")?.classList.toggle("is-disabled", matchedRows.length === 0);

      section.querySelectorAll(".rs-download-csv__download-type").forEach((button) => {
        button.disabled = matchedRows.length === 0;
        button.classList.toggle("is-active", matchedRows.length > 0);
        boxStepHeader.classList.toggle("is-active", matchedRows.length > 0);
      });
      setCsvSyncState("idle");
    }

    function matchedWorkbookBlob() {
      if (!productWorkbook || !productHeaderRecord) {
        return makeXlsxBlob([productHeaders].concat(matchedRows));
      }

      return makeMatchedWorkbookBlobFromSource(
        productWorkbook,
        [productHeaderRecord].concat(matchedRowRecords)
      );
    }

    async function ensureMatchedWorkbook() {
      if (!matchedRows.length) await applySkus();
      return matchedWorkbookBlob();
    }

    applyButton?.addEventListener("click", async () => {
      try {
        parsedSkus = parseSkuText(textarea?.value);
        if (parsedSkus.length) setTextMode();
        await applySkus();
      } catch (error) {
        setCsvSyncState("error", error?.message || "Unable to sync product list.");
        showCsvError(error);
      }
    });

    textarea?.addEventListener("input", () => {
      if (textarea.value.trim()) setTextMode();
    });

    textarea?.addEventListener("keydown", async (event) => {
      if (event.key !== "Enter" || (!event.ctrlKey && !event.metaKey)) return;
      event.preventDefault();
      try {
        parsedSkus = parseSkuText(textarea.value);
        if (parsedSkus.length) setTextMode();
        await applySkus();
      } catch (error) {
        setCsvSyncState("error", error?.message || "Unable to sync product list.");
        showCsvError(error);
      }
    });

    viewDetails?.addEventListener("click", () => {
      document.dispatchEvent(new CustomEvent("rs:download-sku:invalid-skus", {
        detail: { skus: missingSkus }
      }));
    });

    section.querySelectorAll("[data-rs-csv-download]").forEach((button) => {
      button.addEventListener("click", async () => {
        if (!matchedRows.length) return;
        const type = button.dataset.rsCsvDownload;
        if (type === "feed") {
          setImageDownloadState(button, "loading");
          try {
            downloadBlob("rich-solar-product-feed.xlsx", await ensureMatchedWorkbook());
            setImageDownloadState(button, "complete");
          } catch (error) {
            setImageDownloadState(button, "error");
            showCsvError(error);
          }
          return;
        }

        try {
          if (!matchedImageItems.length) throw new Error("No product image links were found for the matched SKUs.");
          triggerImageDownload(matchedImageItems, "Rich Solar Matched Product Images", "rich-solar-matched-product-images", button);
        } catch (error) {
          showCsvError(error);
        }
      });
    });

    document.querySelector("[data-rs-download-sku-feed-confirm]")?.addEventListener("click", async (event) => {
      if (!csvFeedPending) return;
      const modal = event.target.closest(".rs-sku-modal");
      const format = modal?.querySelector('input[name="rs_sku_feed_format"]:checked')?.value;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (format === "rich-solar") {
        try {
          downloadBlob("rich-solar-product-feed.xlsx", await ensureMatchedWorkbook());
        } catch (error) {
          showCsvError(error);
        }
      }
      csvFeedPending = false;
      if (modal) {
        modal.hidden = true;
        modal.setAttribute("aria-hidden", "true");
        document.documentElement.classList.remove("rs-sku-modal-open");
      }
    }, true);

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-rs-sku-modal-close]")) csvFeedPending = false;
    });

    section.querySelectorAll("[data-rs-csv-dropzone]").forEach((dropzone) => {
      const input = dropzone.querySelector("[data-rs-csv-file]");
      const fileName = dropzone.querySelector("[data-rs-csv-file-name]");
      const defaultText = fileName?.textContent || "";

      function setFileName(file) {
        if (!fileName) return;
        fileName.textContent = file?.name || defaultText;
      }

      async function handleFile(file) {
        if (!file) return;
        setUploadMode();
        setFileName(file);
        try {
          const isXlsx = /\.xlsx$/i.test(file.name);
          const rows = isXlsx ? await readXlsxRows(file) : parseCsvRows(await file.text());
          parsedSkus = extractSkusFromRows(rows, { skipFirstRow: true });
          await applySkus();
        } catch (error) {
          setCsvSyncState("error", error?.message || "Unable to sync product list.");
          showCsvError(error);
        }
      }

      input?.addEventListener("change", () => {
        handleFile(input.files?.[0]);
      });

      ["dragenter", "dragover"].forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          dropzone.classList.add("is-dragover");
        });
      });

      ["dragleave", "drop"].forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          dropzone.classList.remove("is-dragover");
        });
      });

      dropzone.addEventListener("drop", (event) => {
        const file = event.dataTransfer?.files?.[0];
        if (!file || !input) return;

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        input.files = dataTransfer.files;
        handleFile(file);
      });
    });
  });

  document.querySelectorAll(".rs-download-sku").forEach((section) => {
    const toolbar = section.querySelector("[data-rs-download-sku-toolbar]");
    if (!toolbar) return;

    const selectAll = toolbar.querySelector("[data-rs-download-sku-select-all]");
    const totalEls = toolbar.querySelectorAll("[data-rs-download-sku-total]");
    const selectedEls = toolbar.querySelectorAll("[data-rs-download-sku-selected]");
    const imageButton = toolbar.querySelector("[data-rs-download-sku-images]");
    const feedButton = toolbar.querySelector("[data-rs-download-sku-feed]");
    const feedModal = document.querySelector('[data-rs-sku-modal="feed"]');
    const invalidModal = document.querySelector('[data-rs-sku-modal="invalid"]');
    const productModal = document.querySelector('[data-rs-sku-modal="product"]');
    const productModalContent = productModal?.querySelector("[data-rs-sku-product-modal-content]");
    const invalidSkuList = invalidModal?.querySelector("[data-rs-invalid-sku-list]");
    const invalidSkuDownload = invalidModal?.querySelector("[data-rs-download-invalid-skus]");
    const feedConfirm = feedModal?.querySelector("[data-rs-download-sku-feed-confirm]");
    const productListUrl = normalizeProductListUrl(section.dataset.productListUrl);
    const productQuickViewCache = new Map();
    let updateFrame = 0;
    let invalidSkus = [];
    const mobileToolbarQuery = window.matchMedia("(max-width: 750px)");
    let toolbarTriggerY = 0;
    let toolbarStickyFrame = 0;

    function setMobileToolbarFixed(shouldFix) {
      toolbar.classList.toggle("rs-download-sku-toolbar_mobile", shouldFix);
      toolbar.classList.toggle("is-bottom-fixed", shouldFix);
    }

    function measureMobileToolbarTrigger() {
      const wasFixed = toolbar.classList.contains("is-bottom-fixed");

      if (wasFixed) setMobileToolbarFixed(false);
      toolbarTriggerY = toolbar.getBoundingClientRect().top + window.scrollY;
      if (wasFixed) setMobileToolbarFixed(true);
    }

    function updateMobileToolbarPosition() {
      if (!mobileToolbarQuery.matches) {
        setMobileToolbarFixed(false);
        toolbarTriggerY = 0;
        return;
      }

      if (!toolbarTriggerY) measureMobileToolbarTrigger();

      const isFixed = toolbar.classList.contains("is-bottom-fixed");
      const shouldFix = isFixed
        ? window.scrollY >= toolbarTriggerY - 8
        : window.scrollY >= toolbarTriggerY;

      setMobileToolbarFixed(shouldFix);
    }

    function requestMobileToolbarUpdate() {
      if (toolbarStickyFrame) return;

      toolbarStickyFrame = requestAnimationFrame(() => {
        toolbarStickyFrame = 0;
        updateMobileToolbarPosition();
      });
    }

    function resetMobileToolbarPosition() {
      toolbarTriggerY = 0;
      requestMobileToolbarUpdate();
    }

    window.addEventListener("scroll", requestMobileToolbarUpdate, { passive: true });
    window.addEventListener("resize", resetMobileToolbarPosition, { passive: true });
    mobileToolbarQuery.addEventListener?.("change", resetMobileToolbarPosition);
    requestAnimationFrame(resetMobileToolbarPosition);

    function getActiveScope() {
      const searchGrid = section.querySelector(".search-results-grid");
      if (searchGrid && searchGrid.style.display !== "none") return searchGrid;

      return Array.from(section.querySelectorAll(".content-grid")).find((grid) => {
        return grid.style.display !== "none";
      }) || section;
    }

    function getCards() {
      return Array.from(getActiveScope().querySelectorAll("[data-download-sku-card]"));
    }

    function getCheckboxes() {
      return getCards()
        .map((card) => card.querySelector("[data-rs-download-sku-checkbox]"))
        .filter(Boolean);
    }

    function setText(elements, value) {
      elements.forEach((element) => {
        element.textContent = value;
      });
    }

    function updateCounts() {
      updateFrame = 0;
      const checkboxes = getCheckboxes();
      const selectedCount = checkboxes.filter((checkbox) => checkbox.checked).length;

      checkboxes.forEach((checkbox) => {
        checkbox.closest("[data-download-sku-card]")?.classList.toggle("is-selected", checkbox.checked);
      });

      setText(totalEls, String(checkboxes.length));
      setText(selectedEls, String(selectedCount));

      if (selectAll) {
        selectAll.checked = checkboxes.length > 0 && selectedCount === checkboxes.length;
        selectAll.indeterminate = selectedCount > 0 && selectedCount < checkboxes.length;
      }

      if (imageButton) {
        imageButton.classList.toggle("is-active", selectedCount > 0);
        imageButton.disabled = selectedCount === 0;
      }

      if (feedButton) {
        feedButton.classList.toggle("is-active", selectedCount > 0);
        feedButton.disabled = selectedCount === 0;
        const count = feedButton.querySelector("[data-rs-download-sku-feed-count]");
        if (count) count.textContent = String(selectedCount);
      }
    }

    function getSelectedCards() {
      return getCards().filter((card) => {
        return card.querySelector("[data-rs-download-sku-checkbox]")?.checked;
      });
    }

    function getCardSku(card) {
      const dataSku = card.querySelector(".product-info.rs-download-sku-card__body")?.dataset.sku;
      if (dataSku) return dataSku.trim();

      const skuText = card.querySelector(".title_sub")?.textContent || "";
      return skuText.replace(/^.*SKU:\s*/i, "").trim();
    }

    function getCardSkus(card) {
      const dataSkus = card.querySelector(".product-info.rs-download-sku-card__body")?.dataset.variantSkus;
      if (dataSkus) {
        try {
          const skus = JSON.parse(dataSkus);
          if (Array.isArray(skus)) {
            return skus.map((sku) => String(sku || "").trim()).filter(Boolean);
          }
        } catch (error) {
          console.error("Unable to read selected product variant SKUs.", error);
        }
      }

      const fallbackSku = getCardSku(card);
      return fallbackSku ? [fallbackSku] : [];
    }

    function getCardImages(card) {
      try {
        const images = JSON.parse(card.dataset.rsDownloadSkuImages || "[]");
        const sku = getCardSku(card);
        const folder = sku || "sku";
        return Array.isArray(images) ? images.map((image, index) => ({
          url: image.url,
          folder,
          filename: `${folder}_${padImageIndex(index)}`
        })).filter((image) => image.url) : [];
      } catch (error) {
        console.error("Unable to read selected product images.", error);
        return [];
      }
    }

    function downloadTextFile(filename, content, type = "text/plain") {
      const blob = new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    }

    function openModal(modal) {
      if (!modal) return;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("rs-sku-modal-open");
    }

    function closeModal(modal) {
      if (!modal) return;
      modal.setAttribute("aria-hidden", "true");
      window.setTimeout(() => {
        if (modal.getAttribute("aria-hidden") !== "true") return;
        modal.hidden = true;
        if (!document.querySelector(".rs-sku-modal:not([hidden])")) {
          document.documentElement.classList.remove("rs-sku-modal-open");
        }
      }, 220);
    }

    function getQuickViewHandle(link) {
      const card = link.closest("[data-download-sku-card]");
      if (card?.dataset.productHandle) return card.dataset.productHandle;

      try {
        const url = new URL(link.href, window.location.origin);
        const parts = url.pathname.split("/").filter(Boolean);
        const productIndex = parts.indexOf("products");
        return productIndex >= 0 ? parts[productIndex + 1] : "";
      } catch (error) {
        return "";
      }
    }

    function setProductQuickViewContent(html) {
      if (!productModalContent) return;
      productModalContent.innerHTML = html;
      productModalContent.querySelectorAll("script").forEach((script) => script.remove());
    }

    async function openProductQuickView(link) {
      if (!productModal || !productModalContent) return;

      const handle = getQuickViewHandle(link);
      if (!handle) {
        window.location.href = link.href;
        return;
      }

      if (link.classList.contains("is-loading")) return;
      link.classList.add("is-loading");
      link.setAttribute("aria-busy", "true");

      try {
        let html = productQuickViewCache.get(handle);

        if (!html) {
          const url = new URL(`/products/${encodeURIComponent(handle)}`, window.location.origin);
          url.searchParams.set("section_id", "rs-product-quick-view");
          const response = await fetch(url.toString(), {
            headers: { "X-Requested-With": "XMLHttpRequest" }
          });
          if (!response.ok) throw new Error("Unable to load product quick view.");
          html = await response.text();
          if (!html.trim()) throw new Error("Product quick view is empty.");
          productQuickViewCache.set(handle, html);
        }

        setProductQuickViewContent(html);
        openModal(productModal);
      } catch (error) {
        console.error(error);
        setProductQuickViewContent(
          `<div class="rs-sku-product-modal__error">
            <p>Unable to load this product.</p>
            <a class="rs-download-sku-card__quick-view" href="${xmlEscape(link.href)}">Open product page</a>
          </div>`
        );
        openModal(productModal);
      } finally {
        link.classList.remove("is-loading");
        link.setAttribute("aria-busy", "false");
      }
    }

    function renderInvalidSkus(skus) {
      invalidSkus = skus || [];
      if (!invalidSkuList) return;
      invalidSkuList.innerHTML = "";
      invalidSkus.forEach((sku) => {
        const item = document.createElement("p");
        item.textContent = sku;
        invalidSkuList.appendChild(item);
      });
    }

    async function downloadSelectedProductFeed() {
      const skus = getSelectedCards().flatMap(getCardSkus).filter(Boolean);
      if (!skus.length) return;

      const previousDisabled = feedButton?.disabled;
      if (feedButton) {
        feedButton.disabled = true;
        feedButton.classList.add("is-loading");
        feedButton.setAttribute("aria-busy", "true");
      }

      try {
        const result = await matchProductRowsBySku(productListUrl, skus);
        if (!result.matchedRows.length) {
          document.dispatchEvent(new CustomEvent("rs:download-sku:invalid-skus", {
            detail: { skus: result.missingSkus }
          }));
          return;
        }

        downloadBlob(
          "rich-solar-product-feed.xlsx",
          await makeMatchedWorkbookBlobFromSource(
            result.workbook,
            [result.headerRecord].concat(result.matchedRowRecords)
          )
        );

        if (result.missingSkus.length) {
          document.dispatchEvent(new CustomEvent("rs:download-sku:invalid-skus", {
            detail: { skus: result.missingSkus }
          }));
        }
      } catch (error) {
        showCsvError(error);
      } finally {
        if (feedButton) {
          feedButton.disabled = previousDisabled;
          feedButton.classList.remove("is-loading");
          feedButton.setAttribute("aria-busy", "false");
          scheduleUpdateCounts();
        }
      }
    }

    function scheduleUpdateCounts() {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(updateCounts);
    }

    selectAll?.addEventListener("change", () => {
      getCheckboxes().forEach((checkbox) => {
        checkbox.checked = selectAll.checked;
      });
      scheduleUpdateCounts();
    });

    section.addEventListener("change", (event) => {
      if (!event.target.closest("[data-rs-download-sku-checkbox]")) return;
      scheduleUpdateCounts();
    });

    section.addEventListener("click", (event) => {
      if (!event.target.closest(".sub-category-btn, .pagination__show-more")) return;
      window.setTimeout(scheduleUpdateCounts, 0);
    });

    section.addEventListener("click", (event) => {
      const quickView = event.target.closest("[data-rs-download-sku-quick-view]");
      if (!quickView) return;
      event.preventDefault();
      openProductQuickView(quickView);
    });

    imageButton?.addEventListener("click", () => {
      const selectedCards = getSelectedCards();
      const images = selectedCards.flatMap(getCardImages);
      if (!images.length) return;
      triggerImageDownload(images, "Rich Solar Selected Product Images", "rich-solar-selected-product-images", imageButton);
    });

    feedButton?.addEventListener("click", async () => {
      await downloadSelectedProductFeed();
    });

    feedConfirm?.addEventListener("click", async () => {
      await downloadSelectedProductFeed();
      closeModal(feedModal);
    });

    document.addEventListener("rs:download-sku:invalid-skus", (event) => {
      renderInvalidSkus(event.detail?.skus || []);
      openModal(invalidModal);
    });

    invalidSkuDownload?.addEventListener("click", () => {
      if (!invalidSkus.length) return;
      downloadBlob(
        "rich-solar-invalid-skus.xlsx",
        makeXlsxBlob([["SKU"]].concat(invalidSkus.map((sku) => [sku])))
      );
    });

    document.addEventListener("click", (event) => {
      const closeTrigger = event.target.closest("[data-rs-sku-modal-close]");
      if (!closeTrigger) return;
      closeModal(closeTrigger.closest(".rs-sku-modal"));
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      document.querySelectorAll(".rs-sku-modal:not([hidden])").forEach(closeModal);
    });

    section.addEventListener("keyup", (event) => {
      if (!event.target.closest("#rs-search-input, #rs-search-input-mobile, #rs-search-input-desktop")) return;
      scheduleUpdateCounts();
    });

    const observer = new MutationObserver(scheduleUpdateCounts);
    observer.observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"]
    });

    scheduleUpdateCounts();
  });
  });
})();
